import type { Decision, PlanKey, TalkTurn } from './talk-api';

/**
 * Talk it through's conversation, kept in this browser per project, so leaving
 * the page or reloading it picks the conversation back up. Cleared once the
 * solution is created or the designer goes back to the ways to start.
 *
 * Never the credential key files: those stay in the page only. Saving the
 * conversation on the server, shared with the team, is backend work that
 * follows.
 */
export interface SavedTalk<Item> {
  items: Item[];
  turn: TalkTurn | null;
  chosen: Partial<Record<PlanKey, string>>;
  asks: Decision[];
  askAt: number;
  asksSent: boolean;
  title: string;
}

const KEY = 'pro-studio:talk-it-through:';
/** A conversation left this long is a new start next time. */
const KEPT_MS = 7 * 24 * 60 * 60 * 1000;

export const loadTalk = <Item>(
  projectId: string,
  now = Date.now()
): SavedTalk<Item> | null => {
  if (!projectId) return null;
  try {
    const raw = window.localStorage.getItem(KEY + projectId);
    if (!raw) return null;
    const saved = JSON.parse(raw) as SavedTalk<Item> & { savedAt?: number };
    if (!Array.isArray(saved.items) || !saved.items.length) return null;
    if (!saved.savedAt || now - saved.savedAt > KEPT_MS) {
      window.localStorage.removeItem(KEY + projectId);
      return null;
    }
    return {
      items: saved.items,
      turn: saved.turn ?? null,
      chosen: saved.chosen ?? {},
      asks: Array.isArray(saved.asks) ? saved.asks : [],
      askAt: saved.askAt ?? 0,
      asksSent: !!saved.asksSent,
      title: saved.title ?? '',
    };
  } catch {
    return null;
  }
};

export const saveTalk = <Item>(
  projectId: string,
  talk: SavedTalk<Item>,
  now = Date.now()
) => {
  if (!projectId) return;
  try {
    if (!talk.items.length) {
      window.localStorage.removeItem(KEY + projectId);
      return;
    }
    window.localStorage.setItem(
      KEY + projectId,
      JSON.stringify({ ...talk, savedAt: now })
    );
  } catch {
    // Not kept past this page; the conversation itself still works.
  }
};

export const clearTalk = (projectId: string) => {
  try {
    window.localStorage.removeItem(KEY + projectId);
  } catch {
    // Nothing to clear.
  }
};
